import type { Locale } from "../content/types";
import { useLocation } from "react-router-dom";
import { landingByLocale, supportByLocale } from "../content";
import { luxoriaConfig } from "../config";
import { PageMeta } from "../components/PageMeta";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Container } from "../components/Container";
import { Icon } from "../components/Icon";
import { BrandMark } from "../components/BrandMark";

type SupportPageProps = {
  locale: Locale;
};

export function SupportPage({ locale }: SupportPageProps) {
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const contentOnly = query.get("contentOnly") === "1" || query.get("embed") === "1";
  const landing = landingByLocale[locale];
  const content = supportByLocale[locale];

  // FAQ 结构化数据：复用页面问答内容，随预渲染输出，便于搜索引擎与 AI 抽取。
  const faqJsonLd = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: content.faqGroups
      .flatMap((group) => group.items)
      .map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer }
      }))
  });

  return (
    <>
      <PageMeta title={content.seo.title} description={content.seo.description} locale={locale} kind="support" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqJsonLd }} />
      {contentOnly ? null : <Header locale={locale} kind="support" nav={landing.nav} variant="minimal" />}
      <main className="bg-bg-primary py-16 lg:py-20">
        <Container width="support">
          <div className="flex flex-col gap-4">
            <div className="inline-flex w-fit items-center gap-2.5 rounded-full border border-subtle bg-bg-card px-3 py-1.5">
              <BrandMark size={22} />
              <span className="font-display text-sm font-semibold text-text-primary">Luxoria</span>
            </div>
            <h1 className="font-display text-4xl font-bold text-text-primary sm:text-[44px]">{content.title}</h1>
            <p className="text-base leading-relaxed text-text-secondary">{content.subtitle}</p>
          </div>

          <div className="mt-12 flex flex-col gap-10">
            <h2 className="font-display text-2xl font-bold text-text-primary sm:text-[28px]">{content.faqTitle}</h2>
            <div className="grid gap-5 md:grid-cols-2">
              {content.faqGroups.flatMap((group) =>
                group.items.map((item) => (
                  <article key={item.question} className="flex flex-col gap-2.5 rounded-2xl border border-subtle bg-bg-card p-6">
                    <h4 className="font-display text-base font-semibold text-text-primary">{item.question}</h4>
                    <p className="text-sm leading-relaxed text-text-secondary">{item.answer}</p>
                  </article>
                ))
              )}
            </div>
          </div>

          <div className="mt-14 grid gap-6 lg:grid-cols-[420px_1fr]">
            <div className="flex items-center gap-4 rounded-3xl border border-subtle bg-gradient-to-br from-[#13203A] to-bg-card p-7">
              <div className="flex flex-col gap-1">
                {contentOnly ? (
                  <div className="inline-flex items-center gap-2">
                    <BrandMark size={18} />
                    <span className="font-display text-sm font-semibold text-text-primary">Luxoria</span>
                  </div>
                ) : null}
                <h2 className="font-display text-base font-semibold text-text-primary">{content.contactLabel}</h2>
                <p className="text-sm text-text-secondary">
                  <a
                    href={`mailto:${luxoriaConfig.contactEmail}`}
                    className="font-medium text-accent-blue hover:underline"
                  >
                    {luxoriaConfig.contactEmail}
                  </a>
                  {` · ${content.contactNote}`}
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-3.5 rounded-3xl border border-subtle bg-bg-card p-8">
              <h2 className="font-display text-lg font-semibold text-text-primary">{content.issueTitle}</h2>
              <ul className="flex flex-col gap-3">
                {content.issueItems.map((item) => (
                  <li key={item} className="flex items-center gap-2.5 text-sm text-text-secondary">
                    <Icon name="check" size={16} className="shrink-0 text-accent-cyan" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Container>
      </main>
      {contentOnly ? null : <Footer locale={locale} kind="support" content={landing.footer} />}
    </>
  );
}
